import {
  richTextRuns,
  type Calculation,
  type Condition,
  type Field,
} from "@proof/core";
import { Select, Button, Control } from "./components";
const names: Record<string, string> = {
  eq: "equals",
  neq: "does not equal",
  empty: "is empty",
  notEmpty: "is not empty",
  startsWith: "starts with",
  endsWith: "ends with",
  contains: "contains",
  notContains: "does not contain",
  matches: "matches pattern",
  gt: "is greater than",
  lt: "is less than",
  in: "is in list",
};
function text(value: string) {
  return richTextRuns(value)
    .map((run) => run.text)
    .join("");
}
function html(value: string) {
  return /<\/?[a-z][^>]*>/i.test(value);
}
function escaping(value: Calculation): boolean {
  return (
    value.op === "replace" &&
    value.replacements.every(
      (r) =>
        (
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          }) as Record<string, string>
        )[r.find] === r.replacement,
    )
  );
}
export function ruleSummary(
  value: Calculation,
  fields: Field[],
  depth = 0,
): string {
  if (depth > 24) return "…";
  const describe = (child: Calculation) =>
    ruleSummary(child, fields, depth + 1);
  if (value.op === "literal")
    return typeof value.value === "string"
      ? text(value.value)
      : String(value.value ?? "");
  if (value.op === "field")
    return `[${fields.find((f) => f.id === value.fieldId)?.label || value.fieldId}]`;
  if (value.op === "concat") return value.parts.map(describe).join("");
  if (value.op === "replace")
    return escaping(value)
      ? describe(value.input)
      : `Replace text in ${describe(value.input)}`;
  return `If ${conditionSummary(value.when, fields)}, use ${describe(value.then)}; otherwise ${describe(value.else)}`;
}
function conditionSummary(value: Condition, fields: Field[]): string {
  if (value.all)
    return value.all.map((v) => conditionSummary(v, fields)).join(" and ");
  if (value.any)
    return value.any.map((v) => conditionSummary(v, fields)).join(" or ");
  return `${fields.find((f) => f.id === value.field)?.label || value.field} ${names[value.op || "eq"] || value.op}${["empty", "notEmpty"].includes(value.op || "") ? "" : ` ${String(value.value ?? "")}`}`;
}
function SimpleCondition({
  value,
  fields,
  onChange,
  label,
}: {
  value: Condition;
  fields: Field[];
  onChange: (value: Condition) => void;
  label: string;
}) {
  const group = value.all ? "all" : value.any ? "any" : undefined;
  if (group)
    return (
      <div>
        <strong>
          {group === "all"
            ? "All of these conditions"
            : "Any of these conditions"}
        </strong>
        {(value.all || value.any || []).map((child, index) => (
          <SimpleCondition
            key={index}
            value={child}
            fields={fields}
            label={`${label} ${index + 1}`}
            onChange={(next) =>
              onChange({
                [group]: (value.all || value.any || []).map((old, i) =>
                  i === index ? next : old,
                ),
              })
            }
          />
        ))}
      </div>
    );
  return (
    <div className="control-grid">
      <Control label="Variable">
        <Select
          aria-label={`${label} variable`}
          value={value.field}
          onChange={(e) => onChange({ ...value, field: e.target.value })}
        >
          {fields.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </Select>
      </Control>
      <Control label="Comparison">
        <Select
          aria-label={`${label} comparison`}
          value={value.op || "eq"}
          onChange={(e) =>
            onChange({ ...value, op: e.target.value as Condition["op"] })
          }
        >
          {Object.entries(names).map(([key, name]) => (
            <option key={key} value={key}>
              {name}
            </option>
          ))}
        </Select>
      </Control>
      {!["empty", "notEmpty"].includes(value.op || "") && (
        <Control label="Compared value">
          <input
            aria-label={`${label} compared value`}
            value={
              Array.isArray(value.value)
                ? value.value.join(", ")
                : String(value.value ?? "")
            }
            onChange={(e) =>
              onChange({
                ...value,
                value:
                  value.op === "in"
                    ? e.target.value.split(",").map((s) => s.trim())
                    : e.target.value,
              })
            }
          />
        </Control>
      )}
    </div>
  );
}
export function SimpleCalculationEditor({
  value,
  fields,
  onChange,
  label = "Calculation",
}: {
  value: Calculation;
  fields: Field[];
  onChange: (value: Calculation) => void;
  label?: string;
}) {
  const render = (
    node: Calculation,
    update: (next: Calculation) => void,
    path: string,
    depth = 0,
  ): React.ReactNode => {
    if (depth > 24) return <p>Open Advanced to inspect deeper rule steps.</p>;
    const child = (
      next: Calculation,
      change: (value: Calculation) => void,
      name: string,
    ) => render(next, change, `${path} ${name}`, depth + 1);
    if (node.op === "literal") {
      if (typeof node.value === "string" && html(node.value))
        return text(node.value).trim() ? (
          <span style={{ whiteSpace: "pre-wrap" }}>{text(node.value)}</span>
        ) : null;
      return (
        <input
          aria-label={`${path} text`}
          value={String(node.value ?? "")}
          style={{ width: "auto", minWidth: 48, maxWidth: "100%" }}
          onChange={(e) =>
            update({
              ...node,
              value:
                typeof node.value === "number"
                  ? Number(e.target.value)
                  : typeof node.value === "boolean"
                    ? e.target.value === "true"
                    : e.target.value,
            })
          }
        />
      );
    }
    if (node.op === "field")
      return (
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <span>Variable</span>
          <Select
            aria-label={`${path} variable`}
            value={node.fieldId}
            onChange={(e) => update({ ...node, fieldId: e.target.value })}
          >
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </Select>
        </label>
      );
    if (node.op === "replace") {
      if (escaping(node))
        return child(
          node.input,
          (input) => update({ ...node, input }),
          "variable",
        );
      return (
        <details>
          <summary>{ruleSummary(node, fields)}</summary>
          {child(node.input, (input) => update({ ...node, input }), "source")}
          {node.replacements.map((item, index) => (
            <div className="control-grid" key={index}>
              <Control label="Find">
                <input
                  aria-label={`${path} find ${index + 1}`}
                  value={item.find}
                  onChange={(e) =>
                    update({
                      ...node,
                      replacements: node.replacements.map((old, i) =>
                        i === index ? { ...old, find: e.target.value } : old,
                      ),
                    })
                  }
                />
              </Control>
              <Control label="Replace with">
                <input
                  aria-label={`${path} replacement ${index + 1}`}
                  value={item.replacement}
                  onChange={(e) =>
                    update({
                      ...node,
                      replacements: node.replacements.map((old, i) =>
                        i === index
                          ? { ...old, replacement: e.target.value }
                          : old,
                      ),
                    })
                  }
                />
              </Control>
            </div>
          ))}
        </details>
      );
    }
    if (node.op === "concat")
      return (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
          }}
        >
          {node.parts.map((part, index) => (
            <div key={index} style={{ display: "contents" }}>
              {child(
                part,
                (next) =>
                  update({
                    ...node,
                    parts: node.parts.map((old, i) =>
                      i === index ? next : old,
                    ),
                  }),
                `part ${index + 1}`,
              )}
            </div>
          ))}
        </div>
      );
    return (
      <div style={{ display: "grid", gap: 16 }}>
        <section>
          <h4>When</h4>
          <SimpleCondition
            value={node.when}
            fields={fields}
            label={`${path} condition`}
            onChange={(when) => update({ ...node, when })}
          />
        </section>
        <section
          style={{
            padding: 12,
            border: "1px solid var(--line,#c6cfca)",
            borderRadius: 8,
          }}
        >
          <h4>Then use</h4>
          {child(
            node.then,
            (then) => update({ ...node, then }),
            "matching result",
          )}
        </section>
        <section
          style={{
            padding: 12,
            border: "1px solid var(--line,#c6cfca)",
            borderRadius: 8,
          }}
        >
          <h4>Otherwise use</h4>
          {child(
            node.else,
            (otherwise) => update({ ...node, else: otherwise }),
            "other result",
          )}
        </section>
      </div>
    );
  };
  return (
    <div aria-label="Simple rule editor">
      <Control label="Rule type">
        <Select
          aria-label={`${label} operation`}
          value={value.op}
          onChange={(e) => {
            const op = e.target.value;
            onChange(
              op === "field"
                ? { op, fieldId: fields[0]?.id || "" }
                : op === "concat"
                  ? { op, parts: [{ op: "literal", value: "" }] }
                  : op === "if"
                    ? {
                        op,
                        when: { field: fields[0]?.id, op: "notEmpty" },
                        then: { op: "literal", value: "" },
                        else: { op: "literal", value: "" },
                      }
                    : { op: "literal", value: "" },
            );
          }}
        >
          <option value="literal">Text or fixed value</option>
          <option value="field" disabled={!fields.length}>
            Use a variable
          </option>
          <option value="concat">Combine text and variables</option>
          <option value="if" disabled={!fields.length}>
            When / Then / Otherwise
          </option>
          {value.op === "replace" && (
            <option value="replace">Replace text</option>
          )}
        </Select>
      </Control>
      <p className="inspector-note">{ruleSummary(value, fields)}</p>
      {render(value, onChange, label)}
      {value.op === "concat" && value.parts.length < 64 && (
        <div className="button-row">
          <Button
            onClick={() =>
              onChange({
                ...value,
                parts: [...value.parts, { op: "literal", value: "" }],
              })
            }
          >
            Add text
          </Button>
          <Button
            disabled={!fields.length}
            onClick={() =>
              onChange({
                ...value,
                parts: [
                  ...value.parts,
                  { op: "field", fieldId: fields[0]?.id || "" },
                ],
              })
            }
          >
            Add variable
          </Button>
        </div>
      )}
      <p className="muted">
        The original formatting is kept. Open Advanced to edit detailed steps or
        formatting.
      </p>
    </div>
  );
}
